import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

export type QuestionStatus = "open" | "answered";
export type QuestionPriority = "low" | "med" | "high";

interface Person {
  id: string;
  name: string;
  role: string;
  initials: string;
  org: "client" | "nymbl";
}

export type AssigneePerson =
  | {
      kind: "stakeholder";
      id: string;
      name: string;
      role: string;
      org: "client" | "nymbl";
      email: string | null;
    }
  | { kind: "member"; id: string; name: string; email: string }
  | null;

export interface QuestionRow {
  id: string;
  solutionId: string;
  externalKey: string | null;
  sourceId: string | null;
  raisedBy: string | null;
  assignedTo: string | null;
  assignedToUserId: string | null;
  status: QuestionStatus;
  priority: QuestionPriority;
  text: string;
  answer: string | null;
  raisedAt: string;
  createdAt: string;
  raisedByPerson: Person | null;
  assignedToPerson: AssigneePerson;
  source: { id: string; name: string } | null;
  relatedItemIds: string[];
}

export interface QuestionPatchBody {
  status?: QuestionStatus;
  priority?: QuestionPriority;
  assignedToStakeholderId?: string | null;
  assignedToUserId?: string | null;
  text?: string;
  answer?: string | null;
  researchNotes?: string | null;
  contextNote?: string | null;
}

export interface QuestionCreateBody {
  text: string;
  assignedToStakeholderId?: string | null;
  assignedToUserId?: string | null;
  priority: QuestionPriority;
  status: QuestionStatus;
  researchNotes?: string | null;
  contextNote?: string | null;
  relatedItemId?: string | null;
  sourceId?: string | null;
}

const questionKeys = {
  list: (solutionId: string) => ["questions", "list", solutionId] as const,
  detail: (id: string) => ["questions", "detail", id] as const,
} as const;

export function useQuestions(solutionId: string) {
  return useQuery({
    queryKey: questionKeys.list(solutionId),
    queryFn: async (): Promise<QuestionRow[]> => {
      const res = await fetch(`/api/solutions/${solutionId}/questions`);
      if (!res.ok) throw new Error("Failed to fetch questions");
      const json = await res.json();
      return json.data;
    },
    enabled: Boolean(solutionId),
  });
}

export function useQuestion(questionId: string | null) {
  return useQuery({
    queryKey: questionKeys.detail(questionId ?? ""),
    queryFn: async (): Promise<QuestionRow> => {
      const res = await fetch(`/api/questions/${questionId}`);
      if (!res.ok) throw new Error("Failed to fetch question");
      const json = await res.json();
      return json.data;
    },
    enabled: Boolean(questionId),
  });
}

export function useCreateQuestion(solutionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: QuestionCreateBody): Promise<QuestionRow> => {
      const res = await fetch(`/api/solutions/${solutionId}/questions`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      });
      if (!res.ok) throw new Error("Failed to create question");
      const json = await res.json();
      return json.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: questionKeys.list(solutionId) });
      qc.invalidateQueries({ queryKey: ["triage", "detail"] });
    },
    onError: () => toast.error("Failed to create question"),
  });
}

export function usePatchQuestion(solutionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, patch }: { id: string; patch: QuestionPatchBody }) => {
      const res = await fetch(`/api/questions/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(patch),
      });
      if (!res.ok) throw new Error("Failed to update question");
      const json = await res.json();
      return json.data;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: questionKeys.list(solutionId) });
      qc.invalidateQueries({ queryKey: questionKeys.detail(vars.id) });
    },
    onError: () => toast.error("Failed to update question"),
  });
}

export function useDeleteQuestion(solutionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/api/questions/${id}`, { method: "DELETE" });
      if (!res.ok) throw new Error("Failed to delete question");
    },
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: questionKeys.list(solutionId) });
      qc.invalidateQueries({ queryKey: questionKeys.detail(id) });
      qc.invalidateQueries({ queryKey: ["triage", "detail"] });
      toast.success("Question deleted");
    },
    onError: () => toast.error("Failed to delete question"),
  });
}
